import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BatchQualityAssessment from "./BatchQualityAssessment";
import { MainRoutes } from "common/config";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import { Button } from "@mui/material";
import {
  QualityCheckService,
  QualityCheckReportResponse
} from "../batch-extraction/BatchExtractionResults";

const BatchQualityAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batchQualityResults, setBatchQualityResults] = useState<any>(null);

  useEffect(() => {
    const batchGuid = location.state?.batchGUID || location.state?.batchGuid;

    if (batchGuid) {
      loadBatchQualityResults(batchGuid);
    } else {
      setError(
        "No batch GUID provided. Please navigate from the quality check page."
      );
    }
  }, [location.state]);

  // Transform API response to UI format
  const transformApiResponseToUIFormat = (
    apiResponse: QualityCheckReportResponse
  ) => {
    const { finalReport } = apiResponse;

    return {
      executive_summary: {
        summary:
          finalReport.executive_summary ||
          "Quality assessment completed for batch processing",
        total_documents_checked: finalReport.batch_summary.total_documents,
        total_document_types: finalReport.document_type_summary.length,
        documents_passed: finalReport.batch_summary.passed_documents,
        documents_failed: finalReport.batch_summary.failed_documents,
        overall_quality_score:
          finalReport.batch_summary.pass_percentage.toString(),
        critical_issues_count: finalReport.document_type_summary.reduce(
          (total, docType) => total + docType.failed_documents,
          0
        ),
        batch_id: finalReport.batch_summary.batch_id
      },
      document_type_breakdown: finalReport.document_type_summary.map(
        (docType) => ({
          doc_type: docType.document_type,
          total_documents: docType.total_documents,
          documents_passed: docType.passed_documents,
          fields_extracted: docType.field_summary.length,
          field_wise_analysis: docType.field_summary.map((field) => ({
            field_name: field.field_name,
            total_documents: field.total_documents,
            passed_documents: field.passed_documents,
            failed_documents: field.failed_documents,
            pass_percentage: field.pass_percentage,
            failure_comments: field.failure_comments,
            failure_summary: field.failure_summary
              ? {
                  summary: field.failure_summary.summary,
                  common_patterns: field.failure_summary.common_patterns,
                  root_cause: field.failure_summary.root_cause,
                  recommendations: field.failure_summary.recommendations
                }
              : undefined
          }))
        })
      ),
      document_wise_details: finalReport.document_type_summary.flatMap(
        (docType) =>
          docType.document_summary.map((doc) => ({
            doc_id: doc.doc_id.toString(),
            document_name: doc.document_name,
            doc_type: docType.document_type,
            pass_percentage: doc.pass_percentage,
            failures: doc.failures || []
          }))
      ),
      assessment_report: {
        executive_summary: {
          title: "Executive Summary",
          content:
            finalReport.executive_summary ||
            `Batch ${finalReport.batch_summary.batch_id} has a pass percentage of <strong>${finalReport.batch_summary.pass_percentage}%</strong>, with <strong>${finalReport.batch_summary.failed_documents} out of ${finalReport.batch_summary.total_documents}</strong> documents failing validation. Key issues include field-level extraction errors and misaligned validation criteria.`
        },
        overall_recommendations: {
          title: "Overall Recommendations",
          items: finalReport.overall_recommendations || [
            "Review and update validation criteria to better align with real-world data ranges and conditions.",
            "Enhance the extraction logic to prevent duplication and ensure accurate field mapping.",
            "Implement stricter quality checks and automated error detection for critical fields."
          ]
        },
        key_findings: {
          title: "Key Findings",
          items: finalReport.key_findings || [
            `The batch has a pass rate of <strong>${finalReport.batch_summary.pass_percentage}%</strong>, with <strong>${finalReport.batch_summary.failed_documents}</strong> documents failing validation.`,
            "Field failures are commonly caused by data extraction inconsistencies and validation rule misalignments.",
            "Validation criteria may need adjustment to reflect real-world data patterns and business requirements."
          ]
        },
        next_actions: {
          title: "Next Actions",
          items: finalReport.next_actions || [
            "Review and reconfigure extraction processes to eliminate common field-level errors.",
            "Adjust validation ranges and rules to reflect actual data trends and business requirements.",
            "Schedule team review meetings to address recurring issues and implement systematic improvements."
          ]
        }
      }
    };
  };

  const loadBatchQualityResults = async (batchGuid: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await QualityCheckService.fetchQualityCheckReport(
        batchGuid
      );

      if (!response.hasErrors && response.value) {
        const transformedData = transformApiResponseToUIFormat(response.value);
        setBatchQualityResults(transformedData);
      } else {
        setError(
          "No data available from API. " +
            (response.errors?.[0] || "Please try again.")
        );
      }
    } catch (err: any) {
      setError(
        "No data available from API. An error occurred while loading the quality check report."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(MainRoutes.BATCH_EXTRACTION_RESULTS);
  };

  const handleQualityAssessed = () => {
    navigate(MainRoutes.BATCH_EXTRACTION_RESULTS, {
      state: {
        message: "Batch quality assessment completed successfully",
        type: "success"
      }
    });
  };

  if (loading) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="batch-quality-assessment-page">
          <HubHeader
            title="Quality Hub"
            description="Batch extraction results and quality assessment"
            showBackButton={true}
            backRoute={MainRoutes.BATCH_EXTRACTION_RESULTS}
          />
          {/* <div style={{ padding: "2rem", textAlign: "center" }}>
            <div>Loading quality assessment results...</div>
          </div> */}
          <div className="home-loading">
            <div className="loading-spinner">Loading...</div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="batch-quality-assessment-page">
          <HubHeader
            title="Quality Hub"
            description="Batch extraction results and quality assessment"
            showBackButton={true}
            backRoute={MainRoutes.BATCH_EXTRACTION_RESULTS}
          />
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <div style={{ color: "red", marginBottom: "1rem" }}>{error}</div>
            <Button
              variant="contained"
              onClick={() => navigate(MainRoutes.DATA_CLASSIFICATION_HISTORY)}
            >
              Back to Available Classifications
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (!batchQualityResults) {
    return (
      <>
        <HeaderNew showSearch={false} />
        <div className="batch-quality-assessment-page">
          <HubHeader
            title="Quality Hub"
            description="Batch extraction results and quality assessment"
            showBackButton={true}
            backRoute={MainRoutes.BATCH_EXTRACTION_RESULTS}
          />
          <div style={{ padding: "2rem", textAlign: "center" }}>
            No batch quality results found. Please try again.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="batch-quality-assessment-page">
        <HubHeader
          title="Quality Hub"
          description="Batch extraction results and quality assessment"
          showBackButton={true}
          backRoute={MainRoutes.BATCH_EXTRACTION_RESULTS}
        />
        <BatchQualityAssessment
          batchQualityResults={batchQualityResults}
          onBack={handleBack}
          onQualityAssessed={handleQualityAssessed}
        />
      </div>
    </>
  );
};

export default BatchQualityAssessmentPage;
